/**
 * Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
 * This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
 * conditions defined in the file COPYING, which is part of this source code package.
 */
import { v4 as uuidv4 } from 'uuid'
import { ref } from 'vue'

import type {
  ContentBlock,
  ConversationResponse,
  ConversationsApi
} from '@/nonfree/ai/control-plane/api/conversations'

export type ConversationStatus = 'idle' | 'streaming' | 'completed' | 'failed' | 'cancelled'

type TurnEnd = Extract<ContentBlock, { type: 'completed' | 'cancelled' | 'failed' }>

interface TurnStream {
  conversationId: string
  // the cursor of the last event read, where reading goes on after a break
  cursor: number
}

interface Turn {
  prompt: string
  idempotencyKey: string
  // set once the server accepted the turn, so that a retry reads on instead of posting it again
  stream?: TurnStream
}

export function describeError(e: unknown): string {
  if (e instanceof Error) {
    return e.message
  }
  if (typeof e === 'string') {
    return e
  }
  try {
    return JSON.stringify(e)
  } catch {
    return String(e)
  }
}

export type ConversationError =
  // the event stream ended before the turn did
  | { kind: 'incomplete' }
  // the turn failed, with the message the control plane has for the user
  | { kind: 'failed'; message: string }
  | { kind: 'request'; message: string }

export class Conversation {
  public readonly id = ref<string | undefined>()
  public readonly blocks = ref<ContentBlock[]>([])
  public readonly status = ref<ConversationStatus>('idle')
  public readonly error = ref<ConversationError | null>(null)

  private readonly creationKey = uuidv4()
  private unfinishedTurn: Turn | undefined
  private running: AbortController | undefined
  private accepting: Promise<unknown> = Promise.resolve()
  private disposed = false

  public constructor(private readonly api: ConversationsApi) {}

  public static resume(api: ConversationsApi, detail: ConversationResponse): Conversation {
    const conversation = new Conversation(api)
    conversation.id.value = detail.conversation_id
    conversation.blocks.value = [...detail.content]
    const last = detail.content[detail.content.length - 1]
    if (last !== undefined && isTurnEnd(last)) {
      conversation.end(last)
    }
    return conversation
  }

  /** Reads the turn that runs while the conversation is opened, which its read leaves out. */
  public async follow(): Promise<void> {
    const conversationId = this.id.value
    if (conversationId === undefined || this.disposed) {
      return
    }
    const running = new AbortController()
    const { signal } = running
    this.takeOver(running)

    try {
      // The stream replays the conversation from its start up to the end of the running turn.
      let replaced = false
      let end: TurnEnd | undefined
      for await (const { block } of this.api.events(conversationId, 0, signal)) {
        if (!replaced) {
          this.blocks.value = []
          replaced = true
        }
        end = this.apply(block)
      }
      if (end) {
        this.end(end)
      } else {
        this.fail({ kind: 'incomplete' }, signal)
      }
    } catch (e) {
      this.fail({ kind: 'request', message: describeError(e) }, signal)
    } finally {
      running.abort()
    }
  }

  public async send(prompt: string): Promise<void> {
    await this.run({ prompt, idempotencyKey: uuidv4() })
  }

  public async retry(): Promise<void> {
    if (this.unfinishedTurn) {
      await this.run(this.unfinishedTurn)
    }
  }

  public dispose(): void {
    this.disposed = true
    this.running?.abort()
  }

  private async run(turn: Turn): Promise<void> {
    const running = new AbortController()
    const { signal } = running
    // Aborting a turn the server has not accepted yet would lose its prompt, so a turn takes
    // over from the running one only once that one is accepted.
    const accepted = this.accepting.then(async () => this.accept(turn, running))
    this.accepting = accepted.catch(() => {})

    try {
      const stream = await accepted
      for await (const { cursor, block } of this.api.events(
        stream.conversationId,
        stream.cursor,
        signal
      )) {
        stream.cursor = cursor
        const end = this.apply(block)
        if (end) {
          this.end(end)
          this.unfinishedTurn = undefined
          return
        }
      }
      this.fail({ kind: 'incomplete' }, signal)
    } catch (e) {
      this.fail({ kind: 'request', message: describeError(e) }, signal)
    } finally {
      running.abort()
    }
  }

  private fail(error: ConversationError, signal: AbortSignal): void {
    if (!signal.aborted) {
      this.status.value = 'failed'
      this.error.value = error
    }
  }

  private async accept(turn: Turn, running: AbortController): Promise<TurnStream> {
    if (this.disposed) {
      running.abort()
    }
    running.signal.throwIfAborted()
    this.takeOver(running)
    this.unfinishedTurn = turn
    if (turn.stream) {
      return turn.stream
    }

    const conversationId = (this.id.value ??= (
      await this.api.create(this.creationKey, running.signal)
    ).conversation_id)
    const { next_cursor: nextCursor } = await this.api.postTurn(
      conversationId,
      turn.prompt,
      turn.idempotencyKey,
      running.signal
    )
    // The turn's events start with its prompt, whose cursor is nextCursor.
    turn.stream = { conversationId, cursor: nextCursor - 1 }
    return turn.stream
  }

  private takeOver(running: AbortController): void {
    this.running?.abort()
    this.running = running
    this.status.value = 'streaming'
    this.error.value = null
  }

  private end(block: TurnEnd): void {
    this.status.value = block.type
    this.error.value = block.type === 'failed' ? { kind: 'failed', message: block.message } : null
  }

  /** Shows the block, and hands it back if it ends a turn. */
  private apply(block: ContentBlock): TurnEnd | undefined {
    switch (block.type) {
      case 'text':
      case 'thinking':
        this.append(block)
        return undefined
      case 'prompt':
      case 'tool_use':
      case 'tool_result':
        this.blocks.value.push(block)
        return undefined
      case 'completed':
      case 'cancelled':
      case 'failed':
        this.blocks.value.push(block)
        return block
      default:
        console.warn('Skipping a conversation block of unknown type:', block satisfies never)
        return undefined
    }
  }

  // The stream sends a text or thinking block in pieces, each continuing the block before it.
  private append(block: Extract<ContentBlock, { type: 'text' | 'thinking' }>): void {
    const blocks = this.blocks.value
    const last = blocks[blocks.length - 1]
    if (block.type === 'text' && last?.type === 'text') {
      blocks[blocks.length - 1] = { type: 'text', text: last.text + block.text }
    } else if (block.type === 'thinking' && last?.type === 'thinking') {
      blocks[blocks.length - 1] = { type: 'thinking', thinking: last.thinking + block.thinking }
    } else {
      blocks.push(block)
    }
  }
}

function isTurnEnd(block: ContentBlock): block is TurnEnd {
  return block.type === 'completed' || block.type === 'cancelled' || block.type === 'failed'
}
